import { styled } from '@mui/material';
import IcClockFill from '@kep/kcds-icons/IcClockFill';
import IcClockRegular from '@kep/kcds-icons/IcClockRegular';
import IcErrorHexagonFill from '@kep/kcds-icons/IcErrorHexagonFill';
import IcErrorHexagonRegular from '@kep/kcds-icons/IcErrorHexagonRegular';
import IcErrorRhombusFill from '@kep/kcds-icons/IcErrorRhombusFill';
import IcErrorRhombusRegular from '@kep/kcds-icons/IcErrorRhombusRegular';
import IcHeartFill from '@kep/kcds-icons/IcHeartFill';
import IcHeartRegular from '@kep/kcds-icons/IcHeartRegular';
import IcInformationCircleFill from '@kep/kcds-icons/IcInformationCircleFill';
import IcInformationCircleRegular from '@kep/kcds-icons/IcInformationCircleRegular';
import IcMoreCircleHorizontalFill from '@kep/kcds-icons/IcMoreCircleHorizontalFill';
import IcMoreCircleHorizontalRegular from '@kep/kcds-icons/IcMoreCircleHorizontalRegular';
import IcQuestionCircleLargeFill from '@kep/kcds-icons/IcQuestionCircleLargeFill';
import IcQuestionCircleLargeRegular from '@kep/kcds-icons/IcQuestionCircleLargeRegular';
import IcRemoveCircleFill from '@kep/kcds-icons/IcRemoveCircleFill';
import IcRemoveCircleRegular from '@kep/kcds-icons/IcRemoveCircleRegular';
import IcSuccessCircleFill from '@kep/kcds-icons/IcSuccessCircleFill';
import IcSuccessCircleRegular from '@kep/kcds-icons/IcSuccessCircleRegular';
import IcWarningTriangleFill from '@kep/kcds-icons/IcWarningTriangleFill';
import IcWarningTriangleRegular from '@kep/kcds-icons/IcWarningTriangleRegular';

import { CircleProgress } from '../CircleProgress';
import { Spinner } from '../Spinner';

import type { ReactNode } from 'react';
import type { StatusIconProps, StatusType, StatusVariant } from './types';

const StatusIconMap: Record<
  Exclude<StatusType, 'determinate-progress' | 'indeterminate-progress'>,
  Record<StatusVariant, ReactNode>
> = {
  active: {
    filled: <IcSuccessCircleFill />,
    line: <IcSuccessCircleRegular />,
  },
  'active-informative': {
    filled: <IcSuccessCircleFill />,
    line: <IcSuccessCircleRegular />,
  },
  running: {
    filled: <IcMoreCircleHorizontalFill />,
    line: <IcMoreCircleHorizontalRegular />,
  },
  'running-informative': {
    filled: <IcMoreCircleHorizontalFill />,
    line: <IcMoreCircleHorizontalRegular />,
  },
  'running-warning': {
    filled: <IcMoreCircleHorizontalFill />,
    line: <IcMoreCircleHorizontalRegular />,
  },
  error: {
    filled: <IcErrorRhombusFill />,
    line: <IcErrorRhombusRegular />,
  },
  'fatal-error': {
    filled: <IcErrorHexagonFill />,
    line: <IcErrorHexagonRegular />,
  },
  pending: {
    filled: <IcClockFill />,
    line: <IcClockRegular />,
  },
  inactive: {
    filled: <IcRemoveCircleFill />,
    line: <IcRemoveCircleRegular />,
  },
  warning: {
    filled: <IcWarningTriangleFill />,
    line: <IcWarningTriangleRegular />,
  },
  information: {
    filled: <IcInformationCircleFill />,
    line: <IcInformationCircleRegular />,
  },
  done: {
    filled: <IcSuccessCircleFill />,
    line: <IcSuccessCircleRegular />,
  },
  healthy: {
    filled: <IcHeartFill />,
    line: <IcHeartRegular />,
  },
  unknown: {
    filled: <IcQuestionCircleLargeFill />,
    line: <IcQuestionCircleLargeRegular />,
  },
};

// CircleProgress와 Spinner는 xl 사이즈를 지원하지 않으므로 lg로 매핑
const mapSizeForProgress = (size: StatusIconProps['size']) => {
  if (size === 'xl') return 'lg';
  return size;
};

const StatusIcon = (props: StatusIconProps) => {
  const {
    status,
    size,
    variant = 'filled',
    determinateOptions,
    indeterminateOptions,
    className,
  } = props;

  if (status === 'determinate-progress') {
    return (
      <StatusIconRoot className={className} status={status}>
        <CircleProgress size={mapSizeForProgress(size)} {...determinateOptions} />
      </StatusIconRoot>
    );
  }

  if (status === 'indeterminate-progress') {
    return (
      <StatusIconRoot className={className} status={status}>
        <Spinner size={mapSizeForProgress(size)} {...indeterminateOptions} />
      </StatusIconRoot>
    );
  }

  return (
    <StatusIconRoot className={className} status={status}>
      {StatusIconMap[status][variant]}
    </StatusIconRoot>
  );
};

export default StatusIcon;

const StatusIconRoot = styled('div', {
  name: 'Status',
  slot: 'icon',
  shouldForwardProp: prop => !['status'].includes(prop as string),
})<Pick<StatusIconProps, 'className' | 'status'>>``;
